import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { CharacterPlugin } from '@/plugins/character/CharacterPlugin'
import { CapsuleCharacterPresenter } from '@/plugins/character/presentation/CapsuleCharacterPresenter'
import { InvisibleCharacterPresenter } from '@/plugins/character/presentation/InvisibleCharacterPresenter'
import { corePlugins } from '@/plugins/corePlugins'
import type {
  CharacterAnimationController,
  CharacterAnimationUpdateContext,
  CharacterLocomotionState,
  CharacterPresentationFrame,
  CharacterPresenter,
  CharacterPresenterFactoryContext,
  Plugin,
} from '@/shared/types'

/** @internal */
export const TIMMY_ROBOT_MODEL_URL = '/3dmodel/timmyrobot.glb'

/** @internal */
export const Y_BOT_MODEL_URL = TIMMY_ROBOT_MODEL_URL

/** @internal */
export type YBotCharacterModelState = 'loading' | 'ready' | 'error'

/** @internal */
export interface YBotCharacterAsset {
  scene: THREE.Object3D
  animations: THREE.AnimationClip[]
}

/** @internal */
export interface YBotCharacterModelBinding {
  root: THREE.Object3D
  animations: THREE.AnimationClip[]
}

/** @internal */
export interface YBotCharacterPresenterOptions extends CharacterPresenterFactoryContext {
  modelUrl?: string
  modelPromise?: Promise<YBotCharacterAsset>
  scale?: number
  yawOffset?: number
  axisCorrection?: THREE.Quaternion
  localOffset?: THREE.Vector3
  onLoadStateChange?: (state: YBotCharacterModelState, error?: Error) => void
}

/** @internal */
export interface YBotLocomotionClipAliases {
  idle?: string[]
  walk?: string[]
  run?: string[]
  jump?: string[]
  fall?: string[]
  land?: string[]
}

/** @internal */
export interface YBotLocomotionAnimationControllerOptions {
  fadeDuration?: number
  clipAliases?: YBotLocomotionClipAliases
  walkSpeed?: number
  runSpeed?: number
  speedChangeRate?: number
  landingMinHold?: number
  landingMaxHold?: number
}

/** @internal */
export type SpatialCapsuleWalkCharacterPresentation = 'model' | 'capsule' | 'none'

/** @internal */
export interface SpatialCapsuleWalkCharacterPluginOptions {
  characterPresentation?: SpatialCapsuleWalkCharacterPresentation
  modelUrl?: string
  /** 是否让 3DGS 和空间代理网格请求携带跨域 Cookie */
  withCredentials?: boolean
  /**
   * 由应用侧注入的资产插件（点云 / 3DGS 等），默认空。
   * 资产插件由调用方按需组装并从对应子入口导入，避免本函数静态依赖
   * 全部资产插件而拖累 tree-shaking（如仅 3DGS 场景不再打包点云解码链）。
   * 若注入的资产插件中已含 name==='character' 的插件，会被定制角色插件替换。
   */
  assetPlugins?: Plugin[]
  onModelStateChange?: (state: YBotCharacterModelState, error?: Error) => void
}

/** @internal */
export interface YBotLocomotionBlendWeights {
  idle: number
  walk: number
  run: number
}

const MODEL_UP = new THREE.Vector3(0, 1, 0)
const WORLD_UP = new THREE.Vector3(0, 0, 1)
const DEFAULT_AXIS_CORRECTION = new THREE.Quaternion().setFromUnitVectors(MODEL_UP, WORLD_UP)
const DEFAULT_YAW_OFFSET = Math.PI / 2
const DEFAULT_SCALE = 1
const DEFAULT_LOCAL_OFFSET = new THREE.Vector3(0, 0, 0)
const DEFAULT_FADE_DURATION = 0.18
const DEFAULT_WALK_SPEED = 2
const DEFAULT_RUN_SPEED = 5.335
const DEFAULT_SPEED_CHANGE_RATE = 10
const DEFAULT_LANDING_MIN_HOLD = 0.12
const DEFAULT_LANDING_MAX_HOLD = 0.42
const LANDING_CANCEL_SPEED = 1.2
const LOCOMOTION_WEIGHT_EPSILON = 1e-4
const GROUND_LOCOMOTION_STATES = ['idle', 'walk', 'run'] as const
const DEFAULT_CLIP_ALIASES: Required<YBotLocomotionClipAliases> = {
  idle: ['idle', 'Idle'],
  walk: ['Walking', 'walk'],
  run: ['Running', 'running', 'run'],
  jump: ['JumpingUp', 'jump'],
  fall: ['FallingIdle', 'falling', 'fall'],
  land: ['Landing', 'land'],
}

/** @internal */
export class YBotCharacterPresenter implements CharacterPresenter {
  readonly object: THREE.Group
  readonly debugObject: THREE.Object3D | null

  private readonly modelContainer = new THREE.Group()
  private readonly lightingRoot: THREE.Group
  private readonly fallbackPresenter: CapsuleCharacterPresenter
  private readonly modelCorrection: THREE.Quaternion
  private readonly modelOffset: THREE.Vector3
  private readonly modelScale: number
  private readonly bindingPromise: Promise<YBotCharacterModelBinding>
  private readonly onLoadStateChange?: (state: YBotCharacterModelState, error?: Error) => void

  private visible = true
  private loadState: YBotCharacterModelState = 'loading'
  private modelRoot: THREE.Object3D | null = null
  private disposed = false

  constructor(options: YBotCharacterPresenterOptions) {
    this.object = new THREE.Group()
    this.object.name = `yBotCharacter:${options.characterId}`
    options.sceneRoot.add(this.object)

    this.modelContainer.name = `yBotModelContainer:${options.characterId}`
    this.object.add(this.modelContainer)

    this.lightingRoot = createYBotLightingRoot(options.characterId)
    options.sceneRoot.add(this.lightingRoot)

    this.fallbackPresenter = new CapsuleCharacterPresenter({
      characterId: options.characterId,
      sceneRoot: options.sceneRoot,
      debugRoot: options.debugRoot,
      color: '#94a3b8',
      opacity: 0.42,
    })
    this.debugObject = this.fallbackPresenter.debugObject

    this.modelCorrection = createModelCorrection(
      options.axisCorrection ?? DEFAULT_AXIS_CORRECTION,
      options.yawOffset ?? DEFAULT_YAW_OFFSET
    )
    this.modelOffset = options.localOffset?.clone() ?? DEFAULT_LOCAL_OFFSET.clone()
    this.modelScale = options.scale ?? DEFAULT_SCALE
    this.onLoadStateChange = options.onLoadStateChange
    this.onLoadStateChange?.('loading')

    this.bindingPromise = (options.modelPromise ?? loadYBotCharacterAsset(options.modelUrl))
      .then((asset) => this.attachModel(asset))
      .catch((error) => {
        const normalizedError = toError(error)
        if (!this.disposed) {
          this.loadState = 'error'
          this.updateVisibility()
          this.onLoadStateChange?.('error', normalizedError)
        }
        throw normalizedError
      })
    this.bindingPromise.catch(() => {})
    this.updateVisibility()
  }

  whenModelReady(): Promise<YBotCharacterModelBinding> {
    return this.bindingPromise
  }

  update(frame: CharacterPresentationFrame): void {
    this.object.position.copy(frame.anchors.root.position)
    this.object.quaternion.copy(frame.anchors.root.quaternion)
    this.modelContainer.position.copy(this.modelOffset)
    this.modelContainer.quaternion.copy(this.modelCorrection)
    this.modelContainer.scale.setScalar(this.modelScale)
    this.fallbackPresenter.update(frame)
    this.updateVisibility()
  }

  setVisible(visible: boolean): void {
    this.visible = visible
    this.updateVisibility()
  }

  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    if (this.modelRoot) {
      disposeObjectTree(this.modelRoot)
      this.modelRoot = null
    }
    this.fallbackPresenter.dispose()
    this.object.removeFromParent()
    this.object.clear()
    this.lightingRoot.removeFromParent()
    this.lightingRoot.clear()
  }

  private attachModel(asset: YBotCharacterAsset): YBotCharacterModelBinding {
    if (this.disposed) {
      disposeObjectTree(asset.scene)
      throw new Error('YBot presenter was disposed before model loading completed')
    }

    const root = asset.scene
    root.name = 'yBotModel'
    prepareModelForCharacterRendering(root)
    this.modelContainer.add(root)
    this.modelRoot = root
    this.loadState = 'ready'
    this.updateVisibility()
    this.onLoadStateChange?.('ready')
    return {
      root,
      animations: asset.animations,
    }
  }

  private updateVisibility(): void {
    const modelVisible = this.visible && this.loadState === 'ready'
    this.object.visible = modelVisible
    this.fallbackPresenter.setVisible(this.visible && !modelVisible)
  }
}

/** @internal */
export class YBotLocomotionAnimationController implements CharacterAnimationController {
  private readonly fadeDuration: number
  private readonly clipAliases: YBotLocomotionClipAliases
  private readonly walkSpeed: number
  private readonly runSpeed: number
  private readonly speedChangeRate: number
  private readonly landingMinHold: number
  private readonly landingMaxHold: number
  private readonly actions = new Map<string, THREE.AnimationAction>()
  private readonly groundLocomotionClipNames = new Map<
    (typeof GROUND_LOCOMOTION_STATES)[number],
    string
  >()
  private readonly clips: THREE.AnimationClip[] = []

  private mixer: THREE.AnimationMixer | null = null
  private modelRoot: THREE.Object3D | null = null
  private currentAction: THREE.AnimationAction | null = null
  private currentClipName: string | null = null
  private latestFrame: CharacterPresentationFrame | null = null
  private groundLocomotionStarted = false
  private smoothedPlanarSpeed = 0
  private landingHoldRemaining = 0
  private landingHoldElapsed = 0
  private landingClipName: string | null = null
  private disposed = false

  constructor(
    presenter: YBotCharacterPresenter,
    options: YBotLocomotionAnimationControllerOptions = {}
  ) {
    this.fadeDuration = options.fadeDuration ?? DEFAULT_FADE_DURATION
    this.clipAliases = options.clipAliases ?? DEFAULT_CLIP_ALIASES
    this.walkSpeed = options.walkSpeed ?? DEFAULT_WALK_SPEED
    this.runSpeed = options.runSpeed ?? DEFAULT_RUN_SPEED
    this.speedChangeRate = options.speedChangeRate ?? DEFAULT_SPEED_CHANGE_RATE
    this.landingMinHold = options.landingMinHold ?? DEFAULT_LANDING_MIN_HOLD
    this.landingMaxHold = options.landingMaxHold ?? DEFAULT_LANDING_MAX_HOLD
    presenter
      .whenModelReady()
      .then((binding) => {
        this.initializeMixer(binding)
      })
      .catch(() => {})
  }

  update(frame: CharacterPresentationFrame, context?: CharacterAnimationUpdateContext): void {
    if (this.disposed) {
      return
    }

    this.latestFrame = frame
    if (!this.mixer) {
      return
    }

    const delta = Math.max(0, context?.delta ?? 0)
    this.updateAnimationState(frame, delta)
    this.mixer.update(delta)
  }

  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    if (this.mixer) {
      this.mixer.stopAllAction()
      for (const clip of this.clips) {
        this.mixer.uncacheClip(clip)
      }
      if (this.modelRoot) {
        this.mixer.uncacheRoot(this.modelRoot)
      }
    }
    this.actions.clear()
    this.groundLocomotionClipNames.clear()
    this.clips.length = 0
    this.mixer = null
    this.modelRoot = null
    this.currentAction = null
    this.currentClipName = null
    this.groundLocomotionStarted = false
    this.landingHoldRemaining = 0
    this.landingHoldElapsed = 0
    this.landingClipName = null
  }

  private initializeMixer(binding: YBotCharacterModelBinding): void {
    if (this.disposed) {
      return
    }

    this.modelRoot = binding.root
    this.mixer = new THREE.AnimationMixer(binding.root)
    this.clips.push(...binding.animations)
    for (const clip of binding.animations) {
      this.actions.set(clip.name, this.mixer.clipAction(clip))
    }
    this.resolveGroundLocomotionClips()

    if (this.latestFrame) {
      this.updateAnimationState(this.latestFrame, 0)
    }
  }

  private updateAnimationState(frame: CharacterPresentationFrame, delta: number): void {
    if (frame.locomotionState === 'jump' || frame.locomotionState === 'fall') {
      this.clearLandingHold()
      this.transitionToState(frame.locomotionState)
      return
    }

    if (frame.locomotionState === 'land') {
      this.beginLandingState()
      return
    }

    if (this.shouldHoldLanding(frame, delta)) {
      return
    }

    this.applyGroundLocomotionBlend(frame, delta)
  }

  private applyGroundLocomotionBlend(frame: CharacterPresentationFrame, delta: number): void {
    const previousAction = this.currentAction
    if (previousAction) {
      previousAction.fadeOut(this.fadeDuration)
    }
    this.currentAction = null
    this.currentClipName = null

    this.ensureGroundLocomotionActionsPlaying()
    const blendAlpha = delta > 0 ? 1 - Math.exp(-this.speedChangeRate * delta) : 1
    this.smoothedPlanarSpeed = THREE.MathUtils.lerp(
      this.smoothedPlanarSpeed,
      Math.max(0, frame.planarSpeed),
      blendAlpha
    )
    const weights = computeYBotLocomotionBlendWeights(
      this.smoothedPlanarSpeed,
      this.walkSpeed,
      this.runSpeed
    )
    this.setGroundLocomotionWeight('idle', weights.idle)
    this.setGroundLocomotionWeight('walk', weights.walk)
    this.setGroundLocomotionWeight('run', weights.run)
  }

  private transitionToState(state: CharacterLocomotionState): THREE.AnimationAction | null {
    const targetClipName = resolveYBotLocomotionClipName(
      state,
      Array.from(this.actions.keys()),
      this.clipAliases
    )
    if (!targetClipName || targetClipName === this.currentClipName) {
      return this.currentAction
    }

    const nextAction = this.actions.get(targetClipName)
    if (!nextAction) {
      return null
    }

    this.fadeOutGroundLocomotionActions(nextAction)
    this.configureActionForState(nextAction, state, targetClipName)
    nextAction.enabled = true
    nextAction.reset()
    nextAction.play()
    if (this.currentAction && this.currentAction !== nextAction) {
      this.currentAction.crossFadeTo(nextAction, this.fadeDuration, false)
    } else {
      nextAction.fadeIn(this.fadeDuration)
    }

    this.currentAction = nextAction
    this.currentClipName = targetClipName
    return nextAction
  }

  private beginLandingState(): void {
    const action = this.transitionToState('land')
    const clipName = this.currentClipName
    if (!action || !clipName || !isExplicitClipForState('land', clipName, this.clipAliases)) {
      this.clearLandingHold()
      return
    }

    if (this.landingClipName === clipName && this.landingHoldRemaining > 0) {
      return
    }

    const duration = action.getClip().duration
    this.landingClipName = clipName
    this.landingHoldElapsed = 0
    this.landingHoldRemaining = THREE.MathUtils.clamp(
      duration,
      this.landingMinHold,
      this.landingMaxHold
    )
  }

  private shouldHoldLanding(frame: CharacterPresentationFrame, delta: number): boolean {
    if (
      this.landingHoldRemaining <= 0 ||
      !this.landingClipName ||
      this.currentClipName !== this.landingClipName
    ) {
      return false
    }

    this.landingHoldElapsed += delta
    this.landingHoldRemaining = Math.max(0, this.landingHoldRemaining - delta)
    if (this.landingHoldRemaining <= 0) {
      this.clearLandingHold()
      return false
    }

    if (
      this.landingHoldElapsed >= this.landingMinHold &&
      frame.planarSpeed >= LANDING_CANCEL_SPEED
    ) {
      this.clearLandingHold()
      return false
    }

    return true
  }

  private clearLandingHold(): void {
    this.landingHoldRemaining = 0
    this.landingHoldElapsed = 0
    this.landingClipName = null
  }

  private configureActionForState(
    action: THREE.AnimationAction,
    state: CharacterLocomotionState,
    clipName: string
  ): void {
    const oneShot =
      (state === 'jump' || state === 'land') &&
      isExplicitClipForState(state, clipName, this.clipAliases)
    action.setLoop(oneShot ? THREE.LoopOnce : THREE.LoopRepeat, oneShot ? 1 : Infinity)
    action.clampWhenFinished = oneShot
    action.setEffectiveTimeScale(1)
  }

  private resolveGroundLocomotionClips(): void {
    this.groundLocomotionClipNames.clear()
    const usedClipNames = new Set<string>()
    const clipNames = Array.from(this.actions.keys())
    for (const state of GROUND_LOCOMOTION_STATES) {
      const clipName = resolveYBotLocomotionClipName(state, clipNames, this.clipAliases)
      if (!clipName || usedClipNames.has(clipName)) {
        continue
      }

      usedClipNames.add(clipName)
      this.groundLocomotionClipNames.set(state, clipName)
    }
  }

  private ensureGroundLocomotionActionsPlaying(): void {
    for (const clipName of this.groundLocomotionClipNames.values()) {
      const action = this.actions.get(clipName)
      if (!action) {
        continue
      }

      action.enabled = true
      action.setLoop(THREE.LoopRepeat, Infinity)
      action.clampWhenFinished = false
      if (!this.groundLocomotionStarted) {
        action.reset()
      }
      action.play()
    }
    this.groundLocomotionStarted = true
  }

  private setGroundLocomotionWeight(
    state: (typeof GROUND_LOCOMOTION_STATES)[number],
    weight: number
  ): void {
    const clipName = this.groundLocomotionClipNames.get(state)
    const action = clipName ? this.actions.get(clipName) : null
    if (!action) {
      return
    }

    action.enabled = weight > LOCOMOTION_WEIGHT_EPSILON
    action.setEffectiveWeight(weight)
    action.setEffectiveTimeScale(1)
  }

  private fadeOutGroundLocomotionActions(exceptAction?: THREE.AnimationAction): void {
    for (const clipName of this.groundLocomotionClipNames.values()) {
      const action = this.actions.get(clipName)
      if (!action || action === exceptAction) {
        continue
      }

      action.fadeOut(this.fadeDuration)
      action.setEffectiveWeight(0)
    }
  }
}

/** @internal */
export function createSpatialCapsuleWalkPlugins(
  options: SpatialCapsuleWalkCharacterPluginOptions = {}
): Plugin[] {
  const presentation = options.characterPresentation ?? 'model'
  const characterPlugin = new CharacterPlugin({
    presenterFactory: (context) => {
      if (presentation === 'none') {
        return new InvisibleCharacterPresenter(context)
      }
      if (presentation === 'capsule') {
        return new CapsuleCharacterPresenter(context)
      }

      return new YBotCharacterPresenter({
        ...context,
        modelUrl: options.modelUrl,
        onLoadStateChange: options.onModelStateChange,
      })
    },
    animationControllerFactory: ({ presenter }) =>
      presenter instanceof YBotCharacterPresenter
        ? new YBotLocomotionAnimationController(presenter)
        : null,
  })
  let replacedCharacterPlugin = false
  const plugins = [
    ...corePlugins({ withCredentials: options.withCredentials }),
    ...(options.assetPlugins ?? []),
  ].map((plugin) => {
    if (plugin.name !== 'character') {
      return plugin
    }

    replacedCharacterPlugin = true
    return characterPlugin
  })

  if (!replacedCharacterPlugin) {
    plugins.push(characterPlugin)
  }

  return plugins
}

/** @internal */
export function resolveYBotLocomotionClipName(
  state: CharacterLocomotionState,
  clipNames: readonly string[],
  aliases: YBotLocomotionClipAliases = DEFAULT_CLIP_ALIASES
): string | null {
  const exactNames = new Set(clipNames)
  const lowerNameMap = new Map(clipNames.map((name) => [name.toLowerCase(), name]))
  const candidates = aliases[state] ?? DEFAULT_CLIP_ALIASES[state]
  for (const candidate of candidates) {
    if (exactNames.has(candidate)) {
      return candidate
    }

    const lowerMatch = lowerNameMap.get(candidate.toLowerCase())
    if (lowerMatch) {
      return lowerMatch
    }
  }

  if (state !== 'idle') {
    return resolveYBotLocomotionClipName('idle', clipNames, aliases)
  }

  return null
}

function isExplicitClipForState(
  state: CharacterLocomotionState,
  clipName: string,
  aliases: YBotLocomotionClipAliases
): boolean {
  const candidates = aliases[state] ?? DEFAULT_CLIP_ALIASES[state]
  return candidates.some((candidate) => candidate.toLowerCase() === clipName.toLowerCase())
}

/** @internal */
export function computeYBotLocomotionBlendWeights(
  planarSpeed: number,
  walkSpeed = DEFAULT_WALK_SPEED,
  runSpeed = DEFAULT_RUN_SPEED
): YBotLocomotionBlendWeights {
  const safeSpeed = Number.isFinite(planarSpeed) ? Math.max(0, planarSpeed) : 0
  const safeWalkSpeed = Math.max(LOCOMOTION_WEIGHT_EPSILON, walkSpeed)
  const safeRunSpeed = Math.max(safeWalkSpeed + LOCOMOTION_WEIGHT_EPSILON, runSpeed)
  if (safeSpeed <= LOCOMOTION_WEIGHT_EPSILON) {
    return {
      idle: 1,
      walk: 0,
      run: 0,
    }
  }

  if (safeSpeed < safeWalkSpeed) {
    const walkWeight = THREE.MathUtils.clamp(safeSpeed / safeWalkSpeed, 0, 1)
    return {
      idle: 1 - walkWeight,
      walk: walkWeight,
      run: 0,
    }
  }

  const runWeight = THREE.MathUtils.clamp(
    (safeSpeed - safeWalkSpeed) / (safeRunSpeed - safeWalkSpeed),
    0,
    1
  )
  return {
    idle: 0,
    walk: 1 - runWeight,
    run: runWeight,
  }
}

function loadYBotCharacterAsset(modelUrl = Y_BOT_MODEL_URL): Promise<YBotCharacterAsset> {
  const loader = new GLTFLoader()
  return loader.loadAsync(modelUrl).then((gltf) => ({
    scene: gltf.scene,
    animations: gltf.animations,
  }))
}

function createModelCorrection(
  axisCorrection: THREE.Quaternion,
  yawOffset: number
): THREE.Quaternion {
  return new THREE.Quaternion().setFromAxisAngle(WORLD_UP, yawOffset).multiply(axisCorrection)
}

function createYBotLightingRoot(characterId: string): THREE.Group {
  const root = new THREE.Group()
  root.name = `yBotLighting:${characterId}`

  const hemisphere = new THREE.HemisphereLight(0xffffff, 0x475569, 1.25)
  hemisphere.name = `yBotHemisphereLight:${characterId}`

  const key = new THREE.DirectionalLight(0xffffff, 1.6)
  key.name = `yBotKeyLight:${characterId}`
  key.position.set(3, -4, 6)

  const fill = new THREE.DirectionalLight(0xdbeafe, 0.7)
  fill.name = `yBotFillLight:${characterId}`
  fill.position.set(-4, 3, 4)

  root.add(hemisphere, key, fill)
  return root
}

function prepareModelForCharacterRendering(root: THREE.Object3D): void {
  root.traverse((object) => {
    object.frustumCulled = false
    if (!isMeshLike(object)) {
      return
    }

    object.castShadow = true
    object.receiveShadow = true
    setMaterialDepthTest(object.material)
  })
}

function setMaterialDepthTest(material: THREE.Material | THREE.Material[] | undefined): void {
  if (!material) {
    return
  }

  const materials = Array.isArray(material) ? material : [material]
  for (const entry of materials) {
    entry.depthTest = true
    entry.depthWrite = true
    entry.colorWrite = true
    entry.needsUpdate = true
  }
}

function disposeObjectTree(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>()
  const materials = new Set<THREE.Material>()
  const textures = new Set<THREE.Texture>()

  root.traverse((object) => {
    if (!isMeshLike(object)) {
      return
    }

    if (object.geometry) {
      geometries.add(object.geometry)
    }
    collectMaterials(object.material, materials, textures)
  })

  root.removeFromParent()
  root.clear()
  for (const texture of textures) {
    texture.dispose()
  }
  for (const material of materials) {
    material.dispose()
  }
  for (const geometry of geometries) {
    geometry.dispose()
  }
}

function collectMaterials(
  material: THREE.Material | THREE.Material[],
  materials: Set<THREE.Material>,
  textures: Set<THREE.Texture>
): void {
  const materialList = Array.isArray(material) ? material : [material]
  for (const item of materialList) {
    materials.add(item)
    for (const value of Object.values(item as unknown as Record<string, unknown>)) {
      if (value instanceof THREE.Texture) {
        textures.add(value)
      }
    }
  }
}

function isMeshLike(object: THREE.Object3D): object is THREE.Mesh | THREE.SkinnedMesh {
  return object instanceof THREE.Mesh || object instanceof THREE.SkinnedMesh
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}
